/**
 * EstimatesSummaryWidget — pixel-matched to the Penpot "Estimates" mockup.
 * Vertical bar chart of $ amounts per estimate status (Draft / Waiting /
 * Won / Conv. / Lost). Replaces the earlier StatusList rendering — Penpot
 * went with a chart here.
 *
 * Colors + short labels come from the bootstrap catalog
 * (display.barColors keyed by status_id; display.shortLabels for the
 * x-axis text). Falls back to standard estimate semaphore + the catalog's
 * full status label if either is missing.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import {
  BarChartCard,
  ChartBarIcon,
  RefreshButton,
  WidgetCard,
} from "components/dashboard";
import { useEstimatesSummary, formatQueryError } from "../hooks";
import type { LayoutItem, StatusEntry, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

// Fallback bar colors per Penpot if the catalog override is missing.
const FALLBACK_COLORS: Record<number, string> = {
  1: "156,163,175",   // Draft — gray-400
  3: "250,204,21",    // Waiting For Response — yellow-400
  5: "34,197,94",     // Won — green-500
  16: "59,130,246",   // Converted to Job — blue-500
  4: "239,68,68",     // Lost — red-500
};

// Fallback short labels for the x-axis when display.shortLabels is missing.
const FALLBACK_LABELS: Record<number, string> = {
  1: "Draft",
  3: "Waiting",
  5: "Won",
  16: "Conv.",
  4: "Lost",
};

export function EstimatesSummaryWidget({ widget, layout }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useEstimatesSummary();

  const statuses = (widget.display.statuses as StatusEntry[] | undefined) ?? [];
  const byId = new Map(statuses.map((s) => [s.id, s]));

  // Catalog-provided overrides (Record keys are stringified IDs in JSON).
  const overrideColors =
    (widget.display.barColors as Record<string, string> | undefined) ?? {};
  const overrideLabels =
    (widget.display.shortLabels as Record<string, string> | undefined) ?? {};

  const chartData = (data?.statusBuckets ?? []).map((bucket) => {
    const id = bucket.statusId;
    const status = byId.get(id);
    return {
      label:
        overrideLabels[String(id)] ??
        FALLBACK_LABELS[id] ??
        status?.label ??
        `#${id}`,
      value: bucket.amountCents,
      color: overrideColors[String(id)] ?? FALLBACK_COLORS[id],
    };
  });

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      icon={<ChartBarIcon />}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "estimates-summary"])}
        />
      }
    >
      <BarChartCard
        data={chartData}
        yFormat="currencyCompact"
        height={200}
        minBarHeight={3}
      />
    </WidgetCard>
  );
}
